import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../styles/theme';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';

const RegisterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.light};
`;

const RegisterCard = styled(Card)`
  max-width: 400px;
  width: 100%;
`;

const RegisterHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.gray};
  }
`;

const RegisterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.radii.small};
  font-size: ${theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.small};
  margin: ${theme.spacing.xs} 0 0 0;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.small};
  
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
    margin-left: ${theme.spacing.xs};
  }
`;

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  
  const { register, loading, error } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    if (!name || !email || !password || !confirmPassword) {
      setFormError('Please fill out all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    
    try {
      console.log('Submitting registration form with:', { name, email });
      await register(name, email, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Registration form error:', err);
      setFormError(err.message || 'Failed to register');
    }
  };
  
  return (
    <RegisterContainer>
      <RegisterCard>
        <RegisterHeader>
          <h1>Create Account</h1>
          <p>Sign up for The Faithful Storyteller</p>
        </RegisterHeader>
        
        <RegisterForm onSubmit={handleSubmit}>
          {(formError || error) && (
            <ErrorMessage>{formError || error}</ErrorMessage>
          )}
          
          <FormGroup>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </FormGroup>
          
          <Button type="submit" fullWidth disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </RegisterForm>
        
        <LoginLink>
          Already have an account?
          <Link to="/login">Sign In</Link>
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register; 